import {useCallback, useEffect, useRef, useState} from 'react'
import {QuotesClient} from '../../../infrastracture/api/QuotesClient'
import {
  QuoteStatusClient,
  QuoteStatusQueryParams,
} from '../../../infrastracture/api/QuoteStatusClient'
import toast from 'react-hot-toast'

export type QuoteActionPending = null | 'approve' | 'decline'

export type SingleQuoteActionPending = {
  quoteId: string
  kind: 'approve' | 'decline'
} | null

export function useSubmittedQuoteActions(
  enabled: boolean,
  submittedQuotes: any[],
  onSuccess: () => void
) {
  const [approvedStatusId, setApprovedStatusId] = useState<string | null>(null)
  const [declinedStatusId, setDeclinedStatusId] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<QuoteActionPending>(null)
  const [singlePending, setSinglePending] = useState<SingleQuoteActionPending>(null)
  const actionGateRef = useRef(false)

  useEffect(() => {
    if (!enabled) {
      setApprovedStatusId(null)
      setDeclinedStatusId(null)
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        const query = new QuoteStatusQueryParams()
        const res = await QuoteStatusClient.List(
          query.Paginate(0, 10000).Sort('name', 'asc').WithActive(true)
        )
        if (cancelled || !res.successful) return
        const rows = Array.isArray(res.data?.data) ? res.data.data : []
        let approved: string | null = null
        let declined: string | null = null
        for (const item of rows) {
          const n = String(item.name || '').toLowerCase()
          if (n === 'approved') approved = item.id
          else if (n === 'declined') declined = item.id
        }
        setApprovedStatusId(approved)
        setDeclinedStatusId(declined)
      } catch (e) {
        console.error(e)
        if (!cancelled) {
          setApprovedStatusId(null)
          setDeclinedStatusId(null)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [enabled])

  const anyActionBusy = pendingAction !== null || singlePending !== null

  const handleApprove = useCallback(async () => {
    if (!approvedStatusId || actionGateRef.current || submittedQuotes.length === 0) return
    actionGateRef.current = true
    setPendingAction('approve')
    try {
      for (const q of submittedQuotes) {
        const res = await QuotesClient.Update(
          q.id,
          q.name,
          q.description,
          q.referenceNumber,
          q.estimate,
          approvedStatusId,
          q.chargeQuoteProperties
        )
        if (!res.successful) {
          toast.error('Could not approve one or more quotes.')
          return
        }
      }
      onSuccess()
    } catch (e) {
      console.error(e)
      toast.error('Could not approve quotes.')
    } finally {
      actionGateRef.current = false
      setPendingAction(null)
    }
  }, [approvedStatusId, submittedQuotes, onSuccess])

  const handleDecline = useCallback(async () => {
    if (!declinedStatusId || actionGateRef.current || submittedQuotes.length === 0) return
    actionGateRef.current = true
    setPendingAction('decline')
    try {
      for (const q of submittedQuotes) {
        const res = await QuotesClient.Decline(q.id, declinedStatusId, '')
        if (!res.successful) {
          toast.error('Could not decline one or more quotes.')
          return
        }
      }
      onSuccess()
    } catch (e) {
      console.error(e)
      toast.error('Could not decline quotes.')
    } finally {
      actionGateRef.current = false
      setPendingAction(null)
    }
  }, [declinedStatusId, submittedQuotes, onSuccess])

  const handleApproveOne = useCallback(
    async (quote: any) => {
      if (
        !quote?.id ||
        !approvedStatusId ||
        actionGateRef.current ||
        String(quote.quoteStatus?.name || '').toLowerCase() !== 'submitted'
      )
        return
      const id = String(quote.id)
      actionGateRef.current = true
      setSinglePending({quoteId: id, kind: 'approve'})
      try {
        const res = await QuotesClient.Update(
          quote.id,
          quote.name,
          quote.description,
          quote.referenceNumber,
          quote.estimate,
          approvedStatusId,
          quote.chargeQuoteProperties
        )
        if (!res.successful) {
          toast.error('Could not approve this quote.')
          return
        }
        onSuccess()
      } catch (e) {
        console.error(e)
        toast.error('Could not approve this quote.')
      } finally {
        actionGateRef.current = false
        setSinglePending(null)
      }
    },
    [approvedStatusId, onSuccess]
  )

  const handleDeclineOne = useCallback(
    async (quote: any) => {
      if (
        !quote?.id ||
        !declinedStatusId ||
        actionGateRef.current ||
        String(quote.quoteStatus?.name || '').toLowerCase() !== 'submitted'
      )
        return
      const id = String(quote.id)
      actionGateRef.current = true
      setSinglePending({quoteId: id, kind: 'decline'})
      try {
        const res = await QuotesClient.Decline(quote.id, declinedStatusId, '')
        if (!res.successful) {
          toast.error('Could not decline this quote.')
          return
        }
        onSuccess()
      } catch (e) {
        console.error(e)
        toast.error('Could not decline this quote.')
      } finally {
        actionGateRef.current = false
        setSinglePending(null)
      }
    },
    [declinedStatusId, onSuccess]
  )

  return {
    pendingAction,
    singlePending,
    handleApprove,
    handleDecline,
    handleApproveOne,
    handleDeclineOne,
    approvedStatusId,
    declinedStatusId,
  }
}
